/**
 * The last few seconds of microphone level, as a ring of 0..1 samples.
 *
 * One microphone, one trace: whatever is capturing pushes frame levels in,
 * and every wave on the page draws the same room from the module-level
 * `speechLevelTrace` — the dock's card background and the transcript drawer
 * cannot disagree, and neither has to reach the other's Stimulus controller.
 *
 * Nothing here reads a clock or touches the DOM: one push is one frame.
 */
export class LevelTrace {
  private readonly samples: Float32Array
  private head = 0
  private readonly listeners = new Set<() => void>()

  constructor(size: number) {
    this.samples = new Float32Array(size)
  }

  get size(): number {
    return this.samples.length
  }

  /** Record one frame's level (clamped to 0..1) and tell whoever is drawing. */
  push(level: number) {
    this.samples[this.head] = Math.min(1, Math.max(0, level))
    this.head = (this.head + 1) % this.samples.length
    this.listeners.forEach((listener) => listener())
  }

  /** Sample `index` counting from the oldest still held. */
  at(index: number): number {
    return this.samples[(this.head + index) % this.samples.length]
  }

  /** Back to a flat line (the microphone stopped). */
  clear() {
    this.samples.fill(0)
    this.head = 0
    this.listeners.forEach((listener) => listener())
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
}

// ~3.5 s of history at the capture frame size, which is as much as the dock
// card is wide enough to draw.
export const speechLevelTrace = new LevelTrace(160)

// Normal speech at arm's length is ~0.05–0.3 RMS; 0.15 fills the wave.
const FULL_SCALE_RMS = 0.15

/** Record one capture frame's RMS as a drawable level. */
export function pushSpeechLevel(rms: number) {
  speechLevelTrace.push(rms / FULL_SCALE_RMS)
}
